import { motion, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { Link } from "react-router-dom";

const MotionLink = motion.create(Link);

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return <p className="eyebrow"><span aria-hidden="true" />{children}</p>;
}

export function ChevronDivider({ light = false }: { light?: boolean }) {
  return (
    <motion.svg
      className={`chevron-divider ${light ? "light" : ""}`}
      viewBox="0 0 1200 54"
      preserveAspectRatio="none"
      aria-hidden="true"
      initial={{ pathLength: 0 }}
      whileInView={{ pathLength: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 1.2, ease: "easeInOut" }}
    >
      <motion.path d="M0 27h526l18-18h112l18 18h526" fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      <motion.path d="M544 9l28 36h56l28-36M572 45l28-36 28 36" fill="none" stroke="currentColor" strokeWidth="1" opacity=".42" vectorEffect="non-scaling-stroke" />
    </motion.svg>
  );
}

export function Reveal({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      data-reveal
      initial={reduceMotion ? false : { opacity: 0, y: 24, clipPath: "inset(0 0 12% 0)" }}
      whileInView={{ opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }}
      viewport={{ once: true, amount: 0.16 }}
      transition={{ duration: 0.85, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function TextReveal({ text, as = "h2", className = "", accent = [] }: {
  text: string;
  as?: "h1" | "h2" | "h3";
  className?: string;
  accent?: string[];
}) {
  const reduceMotion = useReducedMotion();
  const Heading = as === "h1" ? motion.h1 : as === "h3" ? motion.h3 : motion.h2;
  const words = text.split(" ");
  return (
    <Heading
      className={`text-reveal ${className}`}
      aria-label={text}
      initial="hidden"
      animate={as === "h1" ? "visible" : undefined}
      whileInView={as === "h1" ? undefined : "visible"}
      viewport={{ once: true, amount: .45 }}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: reduceMotion ? 0 : .055 } } }}
    >
      {words.map((word, index) => (
        <span className="word-clip" key={`${word}-${index}`} aria-hidden="true">
          <motion.span
            className={accent.includes(word) ? "accent" : ""}
            variants={{
              hidden: { y: reduceMotion ? "0%" : "112%", rotate: reduceMotion ? 0 : 2.5 },
              visible: { y: "0%", rotate: 0, transition: { duration: reduceMotion ? 0 : .72, ease: [0.16, 1, 0.3, 1] } },
            }}
          >{word}</motion.span>
        </span>
      ))}
    </Heading>
  );
}

export function MagneticLink({ to, className = "button", children }: { to: string; className?: string; children: React.ReactNode }) {
  const reduceMotion = useReducedMotion();
  const x = useSpring(useMotionValue(0), { stiffness: 240, damping: 18, mass: 0.25 });
  const y = useSpring(useMotionValue(0), { stiffness: 240, damping: 18, mass: 0.25 });
  const move = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (reduceMotion) return;
    const rect = event.currentTarget.getBoundingClientRect();
    x.set((event.clientX - rect.left - rect.width / 2) * 0.14);
    y.set((event.clientY - rect.top - rect.height / 2) * 0.18);
  };
  return <MotionLink to={to} className={className} style={{ x, y }} onMouseMove={move} onMouseLeave={() => { x.set(0); y.set(0); }}>{children}</MotionLink>;
}

export function PageHero({ eyebrow, title, intro, image, imageAlt, children }: {
  eyebrow: string;
  title: string;
  intro: string;
  image?: string;
  imageAlt?: string;
  children?: React.ReactNode;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <section className={`page-hero ${image ? "with-image" : ""}`}>
      <div className="shell page-hero-grid">
        <motion.div className="page-hero-copy" initial={reduceMotion ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7, ease: [0.16, 1, 0.3, 1] }}>
          <Eyebrow>{eyebrow}</Eyebrow>
          <TextReveal as="h1" text={title} />
          <motion.p className="lede" initial={reduceMotion ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .65, delay: .5, ease: [0.16, 1, 0.3, 1] }}>{intro}</motion.p>
          {children && <motion.div initial={reduceMotion ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .55, delay: .64 }}>{children}</motion.div>}
        </motion.div>
        {image && <Reveal className="page-hero-image"><img src={image} alt={imageAlt ?? ""} /></Reveal>}
      </div>
    </section>
  );
}

export function FinalCta({ compact = false }: { compact?: boolean }) {
  return (
    <section className={`final-cta ${compact ? "compact" : ""}`}>
      <div className="shell final-cta-inner">
        <div>
          <Eyebrow>Your room, measured on us</Eyebrow>
          <TextReveal text="Let’s put the right floor under your feet." accent={["right", "floor"]} />
          <p>Show us the room. We’ll check the details, talk through the trade-offs and price the whole job.</p>
        </div>
        <div className="cta-actions">
          <MagneticLink className="button button-light" to="/get-a-quote">Arrange a free measure</MagneticLink>
          <a className="text-link light" href="tel:+441507237890">Or call 01507 237890 <span>↗</span></a>
        </div>
      </div>
    </section>
  );
}

export function Breadcrumbs({ items }: { items: Array<[string, string]> }) {
  return (
    <nav className="breadcrumbs shell" aria-label="Breadcrumb">
      <Link to="/">Home</Link>
      {items.map(([href, label], index) => (
        <span key={href}><span aria-hidden="true">/</span>{index === items.length - 1 ? <strong>{label}</strong> : <Link to={href}>{label}</Link>}</span>
      ))}
    </nav>
  );
}
